import { useState, useEffect, useMemo } from 'react';
import { RefreshCw, FolderKanban, Calendar, Building2, Tag } from 'lucide-react';
import type { ProjectoCampaign, ProjectoStatus } from '../../types/projecto';
import { PROJECTO_STATUS_CONFIG } from '../../types/projecto';
import { projectoCampaignService } from '../../services/projectoService';
import { useModal } from '../../contexts/ModalContext';

function fmt(v: number) {
  return v.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}

function StatusBadge({ status }: { status: ProjectoStatus }) {
  const cfg = PROJECTO_STATUS_CONFIG[status];
  return (
    <span className={`cm-chip ${cfg.bg} ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

const STATUS_ORDER: ProjectoStatus[] = ['Active', 'Draft', 'On Hold', 'Completed', 'Cancelled'];

interface ProjectoListProps {
  externalProjects?: ProjectoCampaign[];
  onEdit?: (project: ProjectoCampaign) => void;
  onRefresh?: () => void;
  searchFilter?: string;
}

export function ProjectoList({ externalProjects, onEdit, onRefresh, searchFilter }: ProjectoListProps) {
  const [internalProjects, setInternalProjects] = useState<ProjectoCampaign[]>([]);
  const [loading, setLoading] = useState(!externalProjects);
  const [statusFilter, setStatusFilter] = useState<ProjectoStatus | 'all'>('all');
  const { showConfirm, toast } = useModal();

  const projects = externalProjects ?? internalProjects;

  const load = async () => {
    if (externalProjects) {
      onRefresh?.();
      return;
    }
    setLoading(true);
    try {
      const data = await projectoCampaignService.getAll();
      setInternalProjects(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!externalProjects) load();
  }, [externalProjects]);

  const filtered = useMemo(() => {
    let list = projects;
    if (statusFilter !== 'all') list = list.filter((p) => p.status === statusFilter);
    if (searchFilter?.trim()) {
      const q = searchFilter.toLowerCase();
      list = list.filter((p) =>
        p.name.toLowerCase().includes(q) ||
        p.client_name.toLowerCase().includes(q) ||
        p.brand.toLowerCase().includes(q) ||
        p.reference.toLowerCase().includes(q) ||
        (p.work_code ?? '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [projects, searchFilter, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts: Partial<Record<ProjectoStatus, number>> = {};
    for (const p of projects) {
      counts[p.status] = (counts[p.status] ?? 0) + 1;
    }
    return counts;
  }, [projects]);

  const handleOpen = (project: ProjectoCampaign) => {
    onEdit?.(project);
  };

  const handleDelete = async (id: string) => {
    const confirmed = await showConfirm({
      title: 'Delete Project?',
      message: 'This project and all its data (P&L items, invoices, activity logs) will be permanently deleted. This cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await projectoCampaignService.delete(id);
      toast.success('Project deleted');
      load();
    } catch {
      toast.error('Failed to delete project');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            statusFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All ({projects.length})
        </button>
        {STATUS_ORDER.map((s) => {
          const cfg = PROJECTO_STATUS_CONFIG[s];
          const count = statusCounts[s] ?? 0;
          if (count === 0) return null;
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                statusFilter === s ? `${cfg.bg} ${cfg.color}` : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cfg.label} ({count})
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-6 h-6 text-gray-300 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <FolderKanban className="w-12 h-12 mb-3 opacity-20" />
          <p className="text-sm font-medium">
            {projects.length === 0 ? 'No projects yet' : 'No projects match your filters'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((project) => (
            <ProjectoCard
              key={project.id}
              project={project}
              onOpen={() => handleOpen(project)}
              onDelete={() => handleDelete(project.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface CardProps {
  project: ProjectoCampaign;
  onOpen: () => void;
  onDelete: () => void;
}

function ProjectoCard({ project, onOpen, onDelete }: CardProps) {
  const statusCfg = PROJECTO_STATUS_CONFIG[project.status];

  const marginLabel = useMemo(() => {
    if (project.budget_enabled && project.budget_amount > 0) {
      return `${fmt(project.budget_amount)} budget`;
    }
    return 'Per-item margin';
  }, [project.budget_enabled, project.budget_amount]);

  return (
    <div
      className="bg-white rounded-card shadow-card ring-1 ring-gray-100 p-5 hover:shadow-card-hover transition-all cursor-pointer group relative"
      onClick={onOpen}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate group-hover:text-blue-700 transition-colors">
            {project.name || 'Untitled'}
          </h3>
          <div className="flex items-center gap-2">
            {project.work_code && (
              <span className="text-xs text-blue-600 font-mono font-semibold">{project.work_code}</span>
            )}
            {project.reference && (
              <span className="text-xs text-gray-400 font-mono">{project.reference}</span>
            )}
          </div>
        </div>
        <StatusBadge status={project.status} />
      </div>

      <div className="space-y-1.5 text-xs text-gray-500">
        {project.client_name && (
          <div className="flex items-center gap-1.5">
            <Building2 className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{project.client_name}{project.brand ? ` · ${project.brand}` : ''}</span>
          </div>
        )}
        {(project.start_date || project.finish_date) && (
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3 h-3 flex-shrink-0" />
            <span>
              {project.start_date ? new Date(project.start_date).toLocaleDateString('pt-PT') : '—'}
              {' → '}
              {project.finish_date ? new Date(project.finish_date).toLocaleDateString('pt-PT') : '—'}
            </span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <Tag className="w-3 h-3 flex-shrink-0" />
          <span className="text-gray-400">{marginLabel}</span>
        </div>
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
        <span className="text-xs text-gray-400">
          {new Date(project.updated_at).toLocaleDateString('pt-PT')}
        </span>
        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="text-xs text-red-400 hover:text-red-600 transition-colors font-medium"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
